import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Plus, Search, Trash2, AlertTriangle, UserRound } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { KundeDateienTab } from "@/components/KundeDateienTab";
import { KundeSendungenTab } from "@/components/KundeSendungenTab";
import { useSearchParams } from "react-router-dom";

const EMPTY = {
  anrede: "", vorname: "", nachname: "", firma: "", email: "", telefon: "",
  rechnungsadresse: { strasse: "", plz: "", ort: "", land: "DE" },
  lieferadresse: null,
  notizen: "",
  auto_storno_ausschluss: false,
};

export default function Kunden() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [params] = useSearchParams();

  // Direktlink /kunden?kunde=<id> (z. B. aus der Versandliste) öffnet die Kundenakte
  useEffect(() => {
    const id = params.get("kunde");
    if (id) api.get(`/kunden/${id}`).then((r) => setSelected(r.data)).catch(() => {});
  }, [params]);

  const load = async () => {
    const r = await api.get("/kunden", { params: q ? { q } : {} });
    setList(r.data);
    if (selected && !r.data.find((k) => k.id === selected.id)) setSelected(null);
  };

  useEffect(() => { load();  }, [q]);

  const openCreate = () => { setEditing(null); setEditForm(EMPTY); setDuplicates([]); setDialogOpen(true); };
  const openEdit = (k) => { setEditing(k); setEditForm({ ...EMPTY, ...k, rechnungsadresse: k.rechnungsadresse || EMPTY.rechnungsadresse }); setDuplicates([]); setDialogOpen(true); };

  const set = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setEditForm((f) => ({ ...f, rechnungsadresse: { ...f.rechnungsadresse, [k]: v } }));

  const checkDupes = async () => {
    if (editing) return;
    if (!editForm.email && !editForm.telefon) return;
    try {
      const r = await api.post("/kunden/duplicate-check", editForm);
      setDuplicates(r.data.duplicates || []);
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const r = await api.patch(`/kunden/${editing.id}`, editForm);
        toast.success("Kunde aktualisiert.");
        setSelected(r.data);
      } else {
        const r = await api.post("/kunden", editForm);
        toast.success(`Kunde ${r.data.kundennummer} angelegt.`);
        setSelected(r.data);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const del = async (k) => {
    if (!window.confirm(`Kunde ${k.kundennummer} löschen?`)) return;
    try {
      await api.delete(`/kunden/${k.id}`);
      toast.success("Kunde gelöscht.");
      setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen."); }
  };

  return (
    <div className="space-y-6" data-testid="kunden-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Kunden</h1>
          <p className="text-sm text-slate-500 mt-1">Verwalten Sie Ihre Kunden inkl. Rechnungs- und Lieferadressen.</p>
        </div>
        <Button onClick={openCreate} data-testid="kunden-add-button"><Plus className="w-4 h-4 mr-2" /> Kunde anlegen</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche nach Name, Firma, Nr., E-Mail…" className="pl-9" data-testid="kunden-search-input" />
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="kunden-list">
            {list.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">Keine Kunden gefunden.</div>}
            {list.map((k) => (
              <button
                key={k.id}
                onClick={() => setSelected(k)}
                data-testid={`kunde-row-${k.kundennummer}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${selected?.id === k.id ? "bg-blue-50" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-slate-500">{k.kundennummer}</div>
                    <div className="text-sm font-semibold text-slate-900 truncate">{k.firma || `${k.vorname} ${k.nachname}`.trim() || "(ohne Namen)"}</div>
                    <div className="text-xs text-slate-500 truncate">{k.email || k.telefon || "—"}</div>
                  </div>
                  {k.data_complete === false && (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
                      title="Kunde hat seine Adresse noch nicht im Portal bestätigt."
                      data-testid={`kunde-incomplete-badge-${k.kundennummer}`}
                    >
                      <AlertTriangle className="w-3 h-3" /> unvollständig
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          {!selected ? (
            <Card className="border-dashed border-slate-300"><CardContent className="p-10 text-center text-slate-500 text-sm"><UserRound className="w-10 h-10 mx-auto mb-3 text-slate-400" />Wählen Sie einen Kunden aus der Liste aus oder legen Sie einen neuen an.</CardContent></Card>
          ) : (
            <Card className="border-slate-200" data-testid="kunde-detail">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-xs text-slate-500">{selected.kundennummer} · angelegt {fmtDateTime(selected.created_at)}</div>
                    <h2 className="text-xl font-semibold text-slate-900 mt-1 flex items-center gap-2" data-testid="kunde-detail-name">
                      {selected.firma || `${selected.vorname} ${selected.nachname}`.trim() || "(ohne Namen)"}
                      {selected.data_complete === false && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="kunde-detail-incomplete-badge">
                          <AlertTriangle className="w-3 h-3" /> Daten unvollständig
                        </span>
                      )}
                    </h2>
                    {selected.firma && (selected.vorname || selected.nachname) && (
                      <div className="text-sm text-slate-600">{selected.anrede} {selected.vorname} {selected.nachname}</div>
                    )}
                    {selected.data_complete === false && (
                      <div className="text-xs text-amber-700 mt-1">Vollständige Adresse wird beim ersten Portalbesuch durch den Kunden ergänzt.</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => openEdit(selected)} data-testid="kunde-edit-button">Bearbeiten</Button>
                    <Button variant="outline" onClick={() => del(selected)} data-testid="kunde-delete-button"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <Tabs defaultValue="uebersicht" key={selected.id}>
                  <TabsList data-testid="kunde-detail-tabs">
                    <TabsTrigger value="uebersicht" data-testid="kunde-tab-uebersicht">Übersicht</TabsTrigger>
                    <TabsTrigger value="dateien" data-testid="kunde-tab-dateien">Dateien</TabsTrigger>
                    <TabsTrigger value="sendungen" data-testid="kunde-tab-sendungen">Sendungen</TabsTrigger>
                  </TabsList>
                  <TabsContent value="uebersicht" className="space-y-5 pt-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">E-Mail</div><div className="text-slate-900">{selected.email || "—"}</div></div>
                  <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Telefon</div><div className="text-slate-900">{selected.telefon || "—"}</div></div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-mono mb-1">Rechnungsadresse</div>
                  <div className="text-sm text-slate-900">
                    {selected.rechnungsadresse?.strasse || "—"}<br />
                    {selected.rechnungsadresse?.plz} {selected.rechnungsadresse?.ort} · {selected.rechnungsadresse?.land || "DE"}
                  </div>
                </div>
                {selected.notizen && (
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-mono mb-1">Notizen</div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{selected.notizen}</div>
                  </div>
                )}
                  </TabsContent>
                  <TabsContent value="dateien" className="pt-2">
                    <KundeDateienTab kundeId={selected.id} />
                  </TabsContent>
                  <TabsContent value="sendungen" className="pt-2">
                    <KundeSendungenTab kundeId={selected.id} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="kunde-dialog">
          <DialogHeader><DialogTitle>{editing ? "Kunde bearbeiten" : "Neuen Kunden anlegen"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {duplicates.length > 0 && (
              <Alert className="bg-amber-50 border-amber-200 text-amber-900" data-testid="duplicate-warning">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  <div className="font-semibold">Möglicher Duplikat gefunden ({duplicates.length}):</div>
                  <ul className="list-disc pl-5 mt-1 text-sm">
                    {duplicates.map((d) => <li key={d.id}>{d.kundennummer} — {d.firma || `${d.vorname} ${d.nachname}`.trim()} · {d.email}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Anrede</Label><Input value={editForm.anrede} onChange={(e) => set("anrede", e.target.value)} placeholder="Herr / Frau" data-testid="kunde-anrede-input" /></div>
              <div className="col-span-3"><Label>Firma</Label><Input value={editForm.firma} onChange={(e) => set("firma", e.target.value)} data-testid="kunde-firma-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vorname</Label><Input value={editForm.vorname} onChange={(e) => set("vorname", e.target.value)} data-testid="kunde-vorname-input" /></div>
              <div><Label>Nachname</Label><Input value={editForm.nachname} onChange={(e) => set("nachname", e.target.value)} data-testid="kunde-nachname-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-Mail</Label><Input type="email" value={editForm.email} onChange={(e) => set("email", e.target.value)} onBlur={checkDupes} data-testid="kunde-email-input" /></div>
              <div><Label>Telefon</Label><Input value={editForm.telefon} onChange={(e) => set("telefon", e.target.value)} onBlur={checkDupes} data-testid="kunde-telefon-input" /></div>
            </div>
            <div>
              <Label>Rechnungsadresse — Straße</Label>
              <Input value={editForm.rechnungsadresse.strasse} onChange={(e) => setAddr("strasse", e.target.value)} data-testid="kunde-strasse-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>PLZ</Label><Input value={editForm.rechnungsadresse.plz} onChange={(e) => setAddr("plz", e.target.value)} data-testid="kunde-plz-input" /></div>
              <div className="col-span-2"><Label>Ort</Label><Input value={editForm.rechnungsadresse.ort} onChange={(e) => setAddr("ort", e.target.value)} data-testid="kunde-ort-input" /></div>
            </div>
            <div>
              <Label>Notizen</Label>
              <Textarea rows={3} value={editForm.notizen} onChange={(e) => set("notizen", e.target.value)} data-testid="kunde-notizen-input" />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={!!editForm.auto_storno_ausschluss} onChange={(e) => set("auto_storno_ausschluss", e.target.checked)} data-testid="kunde-auto-storno-checkbox" />
              <span>Nicht automatisch stornieren <span className="block text-xs text-slate-500">Für abweichende Zahlungskonditionen (z. B. Zahlung auf Rechnung): keine Zahlungsfrist-Erinnerung, keine automatische Stornierung.</span></span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="kunde-save-button">{editing ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
